import React, { createContext, useState, useEffect, useMemo } from "react";
import { registerUser, loginUser, fetchUserDetails, logoutUser } from "../services/authService";
import { getToken, saveToken, clearToken } from "../utils/tokenUtils";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // User data
  const [roles, setRoles] = useState([]); // User roles
  const [permissions, setPermissions] = useState([]); // User permissions
  const [token, setToken] = useState(null); // Auth token
  const [loading, setLoading] = useState(true); // Initial loading state
  const [authError, setAuthError] = useState(null); // Error state

  /**
   * Load user details using the provided token.
   * @param {string} authToken - The token used to fetch user details.
   */
  const loadUserDetails = async (authToken) => {
    try {
      setLoading(true);
      const result = await fetchUserDetails(authToken);
      setUser(result.user);
      setRoles(result.roles || []);
      setPermissions(result.permissions || []);
      setAuthError(null); // Clear any previous errors
    } catch (err) {
      console.error("Failed to load user details:", err.message);
      setAuthError("Session expired. Please log in again.");
      logout();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initialize token and user details on component mount.
   */
  useEffect(() => {
    const savedToken = getToken();
    if (savedToken) {
      setToken(savedToken);
      loadUserDetails(savedToken);
    } else {
      setLoading(false); // No token, so stop loading
    }
  }, []);

  /**
   * Sync logout across browser tabs.
   */
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "token" && !event.newValue) {
        logout(); // Logout user if token is removed
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  /**
   * Register a new user.
   * @param {object} data - Registration data.
   * @returns {Promise<object>} - The registration result.
   */
  const register = async (data) => {
    try {
      const result = await registerUser(data);
      setUser(result.user);
      setToken(result.token);
      saveToken(result.token);
      setAuthError(null);
      return result;
    } catch (err) {
      console.error("Error during registration:", err.message);
      setAuthError(err.message || "Registration failed.");
      throw err;
    }
  };

  /**
   * Log in a user.
   * @param {object} data - Login data.
   * @returns {Promise<object>} - The login result.
   */
  const login = async (data) => {
    try {
      const result = await loginUser(data);
      setUser(result.user);
      setToken(result.token);
      saveToken(result.token);
      setAuthError(null);
      await loadUserDetails(result.token); // Load user details to populate context
      return result;
    } catch (err) {
      console.error("Error during login:", err.message);
      setAuthError(err.message || "Login failed. Please check your credentials.");
      throw err;
    }
  };

  /**
   * Log out the user.
   */
  const logout = async () => {
    try {
      if (token && user) {
        const response = await logoutUser(token, user.id);
        // // console.log("Logout response:", response); // Log the response for debugging
        localStorage.setItem("logoutMessage", "Logged out successfully");
        localStorage.removeItem("authToken", token);
        return response; // Return the logout response
      }
    } catch (err) {
      console.error("Error during logout:", err.message);
      return { message: err.message || "Logout failed." };
    } finally {
      setUser(null);
      setRoles([]);
      setPermissions([]);
      setToken(null);
      clearToken();
    }
  };

  /**
   * Memoize the auth context value to optimize re-renders.
   */
  const authValue = useMemo(
    () => ({
      loadUserDetails,
      user,
      roles,
      permissions,
      token,
      loading,
      authError,
      register,
      login,
      logout,
    }),
    [user, roles, permissions, token, loading, authError]
  );

  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
};
